import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useReceivingStore } from '../store/receivingStore';
import {
  DeliveryTypeEnum,
  PpeComplianceEnum,
  ReceivingItemTypeEnum,
  type CreateReceivingDocumentRequest,
} from '../types/receiving.types';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const itemSchema = z.object({
  netsuiteItemId: z.number(),
  itemCode: z.string(),
  itemDescription: z.string(),
  poQuantity: z.number(),
  quantityReceived: z.number(),
  purchaseOrderQuantity: z.number(), // remainingQuantity — sent to API
  quantityForPutAway: z.number(), // committed by other unposted receiving docs (86d36yqka)
  unitOfMeasure: z.string(),
  itemType: z.nativeEnum(ReceivingItemTypeEnum),
  receivedQuantity: z.coerce.number().nonnegative('Must not be negative'),
  notes: z.string().optional(),
  lotNumber: z.string().optional(),
  expirationDate: z.string().optional(),
  serialNumbers: z.array(z.string()).optional(),
}).refine(
  // Available = remaining (posted) − already committed by other unposted receiving docs.
  (d) => d.receivedQuantity <= d.purchaseOrderQuantity - d.quantityForPutAway,
  { message: 'Exceeds available quantity for this PO line', path: ['receivedQuantity'] },
);

const formSchema = z.object({
  deliveryNumber: z.string().min(1, 'Required').max(100, 'Max 100 characters'),
  deliveryType: z.nativeEnum(DeliveryTypeEnum),
  docDate: z.string().min(1, 'Delivery date is required'),
  timeIn: z.string().min(1, 'Time in is required'),
  timeOut: z.string().min(1, 'Time out is required'),
  letterOfCredit: z.string().optional(),
  vesselName: z.string().optional(),
  billOfLading: z.string().optional(),
  remarks: z.string().optional(),
  ppeCompliance: z.nativeEnum(PpeComplianceEnum, { required_error: 'PPE compliance is required' }),
  items: z.array(itemSchema).superRefine((items, ctx) => {
    items.forEach((item, index) => {
      if (item.receivedQuantity > item.purchaseOrderQuantity) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Cannot exceed remaining quantity (${item.purchaseOrderQuantity})`,
          path: [index, 'receivedQuantity'],
        });
      }
    });
    if (!items.some((i) => i.receivedQuantity > 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one item must have a received quantity greater than 0',
        path: [],
      });
    }
  }),
});

type FormValues = z.infer<typeof formSchema>;

const DELIVERY_TYPE_OPTIONS = [
  { value: DeliveryTypeEnum.Local, label: 'Local' },
  { value: DeliveryTypeEnum.ImportRawMaterials, label: 'Import – Raw Materials' },
  { value: DeliveryTypeEnum.ImportSpareParts, label: 'Import – Spare Parts' },
  { value: DeliveryTypeEnum.Projects, label: 'Projects' },
];

const PPE_COMPLIANCE_OPTIONS = [
  { value: PpeComplianceEnum.Yes, label: 'Yes' },
  { value: PpeComplianceEnum.No, label: 'No' },
  { value: PpeComplianceEnum.NotApplicable, label: 'N/A' },
];

export function ReceivingFormPage() {
  usePageTitle('New Receiving Document');
  const { poId } = useParams<{ poId: string }>();
  const navigate = useNavigate();
  const {
    selectedPO,
    fetchPurchaseOrderById,
    isLoadingPOs,
    poError,
    createReceivingDocument,
    isSubmitting,
    submitError,
    submitFieldErrors,
  } = useReceivingStore();

  const [deliveryType, setDeliveryType] = useState<DeliveryTypeEnum>(DeliveryTypeEnum.Local);
  const [lineFilter, setLineFilter] = useState<'toReceive' | 'all'>('toReceive');

  const { control, register, handleSubmit, watch, setValue, formState: { errors } } =
    useForm<FormValues>({
      resolver: zodResolver(formSchema),
      mode: 'onChange',
      defaultValues: {
        deliveryType: DeliveryTypeEnum.Local,
        items: [],
      },
    });

  const { fields } = useFieldArray({ control, name: 'items' });
  const watchedItems = watch('items');

  useEffect(() => {
    if (!selectedPO && poId) {
      fetchPurchaseOrderById(poId);
    }
  }, [poId, selectedPO, fetchPurchaseOrderById]);

  useEffect(() => {
    if (selectedPO) {
      // Auto-fill delivery type from PO
      const poDeliveryType = selectedPO.deliveryType ?? DeliveryTypeEnum.Local;
      setValue('deliveryType', poDeliveryType);
      setDeliveryType(poDeliveryType);

      // Auto-fill delivery date and time-in from server timestamp
      const serverTime = new Date(selectedPO.serverTimestamp);
      setValue('docDate', serverTime.toLocaleDateString('en-CA')); // YYYY-MM-DD local
      setValue('timeIn', serverTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })); // HH:mm local

      const itemDefaults = selectedPO.lines.map((line) => ({
        netsuiteItemId: line.netsuiteItemId,
        itemCode: line.itemCode,
        itemDescription: line.itemDescription,
        poQuantity: line.poQuantity,
        quantityReceived: line.quantityReceived,
        purchaseOrderQuantity: line.remainingQuantity,
        quantityForPutAway: line.quantityForPutAway,
        unitOfMeasure: line.unitOfMeasure,
        itemType: line.itemType,
        receivedQuantity: 0,
        notes: '',
        lotNumber: '',
        expirationDate: '',
        serialNumbers: [],
      }));
      setValue('items', itemDefaults);
    }
  }, [selectedPO, setValue]);

  async function onSubmit(values: FormValues) {
    if (!selectedPO) return;

    const request: CreateReceivingDocumentRequest = {
      purchaseOrderId: selectedPO.id || undefined,
      netSuitePurchaseOrderId: selectedPO.netSuitePOId,
      purchaseOrderNumber: selectedPO.poNumber,
      vendorName: selectedPO.vendorName,
      deliveryNumber: values.deliveryNumber,
      deliveryType: values.deliveryType,
      docDate: values.docDate || undefined,
      timeIn: selectedPO.serverTimestamp || undefined,
      timeOut: values.timeOut || undefined,
      letterOfCredit: values.letterOfCredit || undefined,
      vesselName: values.vesselName || undefined,
      billOfLading: values.billOfLading || undefined,
      remarks: values.remarks || undefined,
      ppeCompliance: values.ppeCompliance,
      items: values.items.map((item, idx) => ({
        purchaseOrderItemId: selectedPO.lines[idx]?.id || undefined,
        netsuiteItemId: item.netsuiteItemId,
        itemCode: item.itemCode,
        itemDescription: item.itemDescription,
        purchaseOrderQuantity: item.purchaseOrderQuantity,
        receivedQuantity: item.receivedQuantity,
        unitOfMeasure: item.unitOfMeasure,
        itemType: item.itemType,
        notes: item.notes || undefined,
        lotNumber: item.lotNumber || undefined,
        expirationDate: item.expirationDate || undefined,
        serialNumbers: item.serialNumbers?.filter(Boolean) || undefined,
      })),
    };

    try {
      const doc = await createReceivingDocument(request);
      toast.success(`Receiving document ${doc.docNo} created`);
      navigate(`/mro/receiving/${doc.id}`);
    } catch {
      // submitError from store will show
    }
  }

  if (isLoadingPOs) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        Loading purchase order...
      </div>
    );
  }

  if (!selectedPO) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/mro/receiving/new')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        {poError ? (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{poError}</AlertDescription>
          </Alert>
        ) : (
          <div className="text-muted-foreground">Purchase order not found.</div>
        )}
      </div>
    );
  }

  const totalLineCount = fields.length;
  const toReceiveLineCount = fields.filter((f) => f.purchaseOrderQuantity > 0).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="icon" onClick={() => navigate('/mro/receiving/new')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-xl font-semibold">New Receiving Document</h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* PO Header */}
        <Card>
          <CardHeader>
            <CardTitle>{selectedPO.poNumber}</CardTitle>
            <CardDescription>{selectedPO.vendorName}</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div>
              <Label>PO Date</Label>
              <p className="text-sm mt-1">{new Date(selectedPO.poDate).toLocaleDateString()}</p>
            </div>
            <div>
              <Label>Status</Label>
              <p className="text-sm mt-1">{selectedPO.poStatus}</p>
            </div>
            <div>
              <Label>Lines</Label>
              <p className="text-sm mt-1">{selectedPO.lines.length}</p>
            </div>
          </CardContent>
        </Card>

        {/* Delivery Header */}
        <Card>
          <CardHeader>
            <CardTitle>Delivery Information</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="deliveryNumber">
                Delivery / DR Number <span className="text-destructive">*</span>
              </Label>
              <Input
                id="deliveryNumber"
                {...register('deliveryNumber')}
                placeholder="Supplier DR or SI number"
              />
              {errors.deliveryNumber && (
                <p className="text-xs text-destructive">{errors.deliveryNumber.message}</p>
              )}
            </div>

            <div className="space-y-1">
              <Label>Delivery Type</Label>
              <Input
                value={DELIVERY_TYPE_OPTIONS.find((o) => o.value === deliveryType)?.label ?? 'Local'}
                readOnly
                className="bg-muted"
              />
            </div>

            <div className="grid sm:col-span-2 gap-4" style={{ gridTemplateColumns: '2fr 1fr 1fr' }}>
              <div className="space-y-1">
                <Label htmlFor="docDate">Delivery Date</Label>
                <Input id="docDate" type="date" {...register('docDate')} readOnly className="bg-muted" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="timeIn">Time In</Label>
                <Input id="timeIn" type="time" {...register('timeIn')} readOnly className="bg-muted" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="timeOut">
                  Time Out <span className="text-destructive">*</span>
                </Label>
                <Input id="timeOut" type="time" {...register('timeOut')} />
                {errors.timeOut && (
                  <p className="text-xs text-destructive">{errors.timeOut.message}</p>
                )}
              </div>
            </div>

            {deliveryType !== DeliveryTypeEnum.Local && (
              <>
                <div className="space-y-1">
                  <Label htmlFor="letterOfCredit">Letter of Credit</Label>
                  <Input id="letterOfCredit" {...register('letterOfCredit')} placeholder="LC number" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="vesselName">Vessel Name</Label>
                  <Input id="vesselName" {...register('vesselName')} placeholder="Vessel or carrier name" />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="billOfLading">Bill of Lading</Label>
                  <Input id="billOfLading" {...register('billOfLading')} placeholder="B/L number" />
                </div>
              </>
            )}

            <div className="space-y-1 sm:col-span-2">
              <Label htmlFor="remarks">Remarks</Label>
              <Textarea id="remarks" {...register('remarks')} placeholder="Optional delivery remarks" rows={2} />
            </div>

            <div className="space-y-1">
              <Label>
                PPE Requirements Complied <span className="text-destructive">*</span>
              </Label>
              <Controller
                control={control}
                name="ppeCompliance"
                render={({ field }) => (
                  <Select
                    value={field.value != null ? String(field.value) : undefined}
                    onValueChange={(v) => field.onChange(Number(v))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select PPE compliance" />
                    </SelectTrigger>
                    <SelectContent>
                      {PPE_COMPLIANCE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={String(opt.value)}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.ppeCompliance && (
                <p className="text-xs text-destructive">{errors.ppeCompliance.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Line Items Filter */}
        {totalLineCount > 0 && (
          <Tabs value={lineFilter} onValueChange={(v) => setLineFilter(v as 'toReceive' | 'all')}>
            <TabsList>
              <TabsTrigger value="toReceive">To Receive ({toReceiveLineCount})</TabsTrigger>
              <TabsTrigger value="all">All ({totalLineCount})</TabsTrigger>
            </TabsList>
          </Tabs>
        )}

        {/* Empty state when every line is fully received */}
        {lineFilter === 'toReceive' && toReceiveLineCount === 0 && totalLineCount > 0 && (
          <Card>
            <CardContent className="flex flex-col items-center gap-3 py-10 text-center text-muted-foreground">
              <p>All items on this PO have been fully received.</p>
              <Button type="button" variant="outline" size="sm" onClick={() => setLineFilter('all')}>
                Show all items
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Line Items */}
        {fields.map((field, index) => {
          if (lineFilter === 'toReceive' && field.purchaseOrderQuantity <= 0) return null;
          const item = watchedItems[index];
          return (
            <Card key={field.id}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base">{field.itemCode}</CardTitle>
                    <CardDescription>{field.itemDescription}</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    {field.itemType === ReceivingItemTypeEnum.LotNumbered && (
                      <Badge variant="secondary">Lot Numbered</Badge>
                    )}
                    {field.itemType === ReceivingItemTypeEnum.Serialized && (
                      <Badge variant="outline">Serialized</Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
                  <div>
                    <Label className="text-xs text-muted-foreground">PO Qty</Label>
                    <p className="font-medium">
                      {field.poQuantity} {field.unitOfMeasure}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Already Received</Label>
                    <p className="font-medium">
                      {field.quantityReceived} {field.unitOfMeasure}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Available</Label>
                    <p className="font-medium">
                      {(field.purchaseOrderQuantity - field.quantityForPutAway).toFixed(2)} {field.unitOfMeasure}
                    </p>
                    {field.quantityForPutAway > 0 && (
                      <p className="text-[11px] text-warning mt-0.5">
                        {field.purchaseOrderQuantity.toFixed(2)} remaining − {field.quantityForPutAway.toFixed(2)} in&nbsp;progress
                      </p>
                    )}
                  </div>
                  <div className="space-y-1 sm:col-span-2">
                    <Label htmlFor={`items.${index}.receivedQuantity`}>
                      Received Qty <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id={`items.${index}.receivedQuantity`}
                      type="number"
                      step="0.001"
                      max={field.purchaseOrderQuantity - field.quantityForPutAway}
                      {...register(`items.${index}.receivedQuantity`, { valueAsNumber: true })}
                    />
                    {errors.items?.[index]?.receivedQuantity && (
                      <p className="text-xs text-destructive">
                        {errors.items[index]?.receivedQuantity?.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* Notes */}
                <div className="space-y-1">
                  <Label htmlFor={`items.${index}.notes`}>Notes</Label>
                  <Textarea
                    id={`items.${index}.notes`}
                    {...register(`items.${index}.notes`)}
                    placeholder="Add notes for this item (optional)"
                    rows={2}
                  />
                </div>

                {/* Lot number */}
                {field.itemType === ReceivingItemTypeEnum.LotNumbered && (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="space-y-1">
                      <Label htmlFor={`items.${index}.lotNumber`}>
                        Lot Number <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        id={`items.${index}.lotNumber`}
                        {...register(`items.${index}.lotNumber`)}
                        placeholder="Lot number from packaging"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor={`items.${index}.expirationDate`}>Expiration Date</Label>
                      <Input
                        id={`items.${index}.expirationDate`}
                        type="date"
                        {...register(`items.${index}.expirationDate`)}
                      />
                    </div>
                  </div>
                )}

                {/* Serial numbers */}
                {field.itemType === ReceivingItemTypeEnum.Serialized && (item?.receivedQuantity ?? 0) > 0 && (
                  <div className="space-y-2">
                    <Label>
                      Serial Numbers <span className="text-destructive">*</span>
                      <span className="text-muted-foreground text-xs ml-2">(one per unit received)</span>
                    </Label>
                    <Controller
                      control={control}
                      name={`items.${index}.serialNumbers`}
                      render={({ field: serialField }) => {
                        const serials = serialField.value ?? [];
                        const count = Math.max(Math.floor(item?.receivedQuantity ?? 0), serials.length);

                        return (
                          <div className="space-y-2">
                            {Array.from({ length: count }, (_, si) => (
                              <div key={si} className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground w-6">{si + 1}.</span>
                                <Input
                                  placeholder={`Serial #${si + 1}`}
                                  value={serials[si] ?? ''}
                                  onChange={(e) => {
                                    const updated = [...serials];
                                    updated[si] = e.target.value;
                                    serialField.onChange(updated);
                                  }}
                                />
                              </div>
                            ))}
                          </div>
                        );
                      }}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}

        {submitError && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <p>{submitError}</p>
              {submitFieldErrors && Object.keys(submitFieldErrors).length > 0 && (
                <ul className="mt-2 list-disc list-inside space-y-0.5 text-sm">
                  {Object.entries(submitFieldErrors).map(([field, messages]) =>
                    messages.map((msg, i) => (
                      <li key={`${field}-${i}`}>{msg}</li>
                    ))
                  )}
                </ul>
              )}
            </AlertDescription>
          </Alert>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={() => navigate('/mro/receiving/new')}>
            Back
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Submit Receiving Document'}
          </Button>
        </div>
      </form>
    </div>
  );
}
